import { useState, useEffect, useRef } from "react";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import {
  ExternalLink,
  X,
  CreditCard,
  FileText,
  Settings,
  AlertCircle,
} from "lucide-react";

interface CustomerPortalModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  hasActiveSubscription: boolean;
}

export function CustomerPortalModal({
  isOpen,
  onClose,
  onConfirm,
  hasActiveSubscription,
}: CustomerPortalModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);
  const mouseDownTargetRef = useRef<EventTarget | null>(null);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isLoading) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [isOpen, isLoading, onClose]);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await onConfirm();
    } finally {
      setIsLoading(false);
    }
  };

  // Track mousedown to prevent closing when dragging from inside modal
  const handleMouseDown = (e: React.MouseEvent) => {
    mouseDownTargetRef.current = e.target;
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    if (
      !isLoading &&
      e.target === overlayRef.current &&
      mouseDownTargetRef.current === overlayRef.current
    ) {
      onClose();
    }
    mouseDownTargetRef.current = null;
  };

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm"
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <Settings size={20} className="text-primary-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">
              Stripe kundportal
            </h2>
          </div>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="p-1.5 rounded-md hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-5">
          <p className="text-sm text-gray-600">
            Du kommer att dirigeras till Stripes säkra kundportal där du kan
            hantera dina betalningsuppgifter.
          </p>

          {/* Features list */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-gray-900">
              I kundportalen kan du:
            </h3>
            <ul className="space-y-2.5">
              <li className="flex items-start gap-3 text-sm text-gray-600">
                <CreditCard
                  size={18}
                  className="text-gray-400 flex-shrink-0 mt-0.5"
                />
                <span>
                  <strong className="text-gray-900">Betalningsmetod</strong> —
                  Uppdatera eller byt kortnummer
                </span>
              </li>
              <li className="flex items-start gap-3 text-sm text-gray-600">
                <FileText
                  size={18}
                  className="text-gray-400 flex-shrink-0 mt-0.5"
                />
                <span>
                  <strong className="text-gray-900">Fakturor</strong> — Se och
                  ladda ner alla fakturor från Stripe
                </span>
              </li>
              {hasActiveSubscription && (
                <li className="flex items-start gap-3 text-sm text-gray-600">
                  <AlertCircle
                    size={18}
                    className="text-amber-500 flex-shrink-0 mt-0.5"
                  />
                  <span>
                    <strong className="text-gray-900">Avsluta</strong> — Du kan
                    även avsluta prenumerationer i portalen
                  </span>
                </li>
              )}
            </ul>
          </div>

          {/* Info box */}
          <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
            <p>
              Efter att du gjort ändringar klickar du på{" "}
              <strong className="text-gray-900">tillbaka</strong> i portalen för
              att återvända hit. Eventuella ändringar synkroniseras automatiskt.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-5 border-t border-gray-200 bg-gray-50">
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Avbryt
          </Button>
          <Button onClick={handleConfirm} disabled={isLoading}>
            {isLoading ? (
              <Spinner size="sm" />
            ) : (
              <ExternalLink size={16} />
            )}
            Öppna kundportalen
          </Button>
        </div>
      </div>
    </div>
  );
}
